import { useEffect, useState, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import { MiniTrend } from './MiniTrend';
import { TuningTab } from './TuningTab';
import type { LoopMode } from '../../engine/core/actions';
import type { AlarmConfig } from '../../engine/plant/types';

const PRI_COLOUR: Record<string, string> = {
  emergency: 'var(--pri-emergency)',
  high: 'var(--pri-high)',
  medium: 'var(--pri-medium)',
  low: 'var(--pri-low)',
};

function Bar({
  label,
  value,
  lo,
  hi,
  colour,
  marks,
  units,
  decimals,
}: {
  label: string;
  value: number;
  lo: number;
  hi: number;
  colour: string;
  marks?: { v: number; colour: string }[];
  units: string;
  decimals: number;
}): JSX.Element {
  const frac = Math.max(0, Math.min(1, (value - lo) / Math.max(hi - lo, 1e-9)));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 62 }}>
      <div className="num" style={{ fontSize: 14, color: colour }}>
        {value.toFixed(decimals)}
      </div>
      <div
        style={{
          position: 'relative',
          width: 26,
          height: 150,
          background: 'var(--bg-deep)',
          border: '1px solid var(--line-dim)',
          borderRadius: 2,
        }}
      >
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: `${frac * 100}%`,
            background: colour,
            opacity: 0.8,
          }}
        />
        {(marks ?? []).map((m, i) => {
          const f = Math.max(0, Math.min(1, (m.v - lo) / Math.max(hi - lo, 1e-9)));
          return (
            <div
              key={i}
              title={m.v.toFixed(decimals)}
              style={{
                position: 'absolute',
                bottom: `${f * 100}%`,
                left: -4,
                right: -4,
                height: 0,
                borderTop: `2px solid ${m.colour}`,
              }}
            />
          );
        })}
      </div>
      <div className="muted" style={{ fontSize: 10.5 }}>
        {label}
      </div>
      <div className="muted" style={{ fontSize: 9.5 }}>
        {units}
      </div>
    </div>
  );
}

function NumberEntry({
  value,
  decimals,
  disabled,
  onCommit,
  label,
}: {
  value: number;
  decimals: number;
  disabled: boolean;
  onCommit(v: number): void;
  label: string;
}): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => {
    if (draft === null) return;
  }, [draft]);
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}>
      <span className="muted" style={{ width: 24 }}>
        {label}
      </span>
      <input
        style={{ width: 76, textAlign: 'right' }}
        disabled={disabled}
        value={draft ?? value.toFixed(decimals)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== null) {
            const n = Number(draft);
            if (Number.isFinite(n)) onCommit(n);
            setDraft(null);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            setDraft(null);
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
    </label>
  );
}

export function Faceplate({ loopId }: { loopId: string }): JSX.Element {
  const loop = useEngine((s) => s.snap?.loops.find((l) => l.id === loopId));
  const plant = useEngine((s) => s.plant);
  const send = useEngine((s) => s.send);
  const [tab, setTab] = useState<'face' | 'tune' | 'info'>('face');

  if (!loop) return <div style={{ padding: 10 }}>Loading…</div>;

  const alarms: AlarmConfig[] = (plant?.alarms ?? []).filter((a) => a.tag === loop.pvTag);
  const marks = alarms
    .filter((a) => a.limit !== undefined && ['HH', 'H', 'L', 'LL'].includes(a.kind))
    .map((a) => ({ v: a.limit as number, colour: PRI_COLOUR[a.priority] ?? 'var(--line)' }));

  const modeButton = (m: LoopMode, enabled: boolean, why: string): JSX.Element => (
    <button
      key={m}
      className={loop.mode === m ? 'on' : ''}
      disabled={!enabled}
      title={why}
      onClick={() => send({ t: 'setMode', loop: loop.id, mode: m })}
      style={{ flex: 1, padding: '5px 0', fontSize: 11.5 }}
    >
      {m}
    </button>
  );

  return (
    <div style={{ width: 300 }}>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--line-dim)' }}>
        {(['face', 'tune', 'info'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={tab === t ? 'on' : ''}
            style={{ flex: 1, border: 'none', borderRadius: 0, fontSize: 11.5, padding: '5px 0' }}
          >
            {t === 'face' ? 'Faceplate' : t === 'tune' ? 'Tuning' : 'Info'}
          </button>
        ))}
      </div>

      {tab === 'face' && (
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 9 }}>
          {loop.badPv && (
            <div style={{ background: 'var(--pri-high)', color: '#231400', padding: '3px 6px', borderRadius: 3, fontSize: 11, fontWeight: 600 }}>
              PV QUALITY BAD
            </div>
          )}
          {loop.tracking && (
            <div className="muted" style={{ fontSize: 11 }}>
              Tracking — {loop.secondary} is not in CAS, so this loop is following its setpoint.
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
            <Bar
              label="PV"
              value={loop.pv}
              lo={loop.rangeLo}
              hi={loop.rangeHi}
              colour="var(--ok)"
              marks={[...marks, { v: loop.sp, colour: 'var(--accent)' }]}
              units={loop.units}
              decimals={loop.decimals}
            />
            <Bar
              label="SP"
              value={loop.sp}
              lo={loop.rangeLo}
              hi={loop.rangeHi}
              colour="var(--accent)"
              units={loop.units}
              decimals={loop.decimals}
            />
            <Bar
              label="OP"
              value={loop.op}
              lo={0}
              hi={100}
              colour={loop.saturated ? 'var(--pri-medium)' : 'var(--utility)'}
              marks={[
                { v: loop.tuning.opLo, colour: 'var(--line-dim)' },
                { v: loop.tuning.opHi, colour: 'var(--line-dim)' },
              ]}
              units="%"
              decimals={1}
            />
          </div>

          <div style={{ display: 'flex', gap: 5 }}>
            {modeButton('MAN', true, 'Operator writes the output directly')}
            {modeButton('AUTO', true, 'Controller holds the setpoint')}
            {modeButton(
              'CAS',
              loop.primary !== null,
              loop.primary ? `Setpoint comes from ${loop.primary}` : 'No cascade primary on this loop',
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <NumberEntry
              label="SP"
              value={loop.sp}
              decimals={loop.decimals}
              disabled={loop.mode === 'CAS' || loop.inGroup !== null}
              onCommit={(v) => send({ t: 'setSp', loop: loop.id, value: v })}
            />
            <NumberEntry
              label="OP"
              value={loop.op}
              decimals={1}
              disabled={loop.mode !== 'MAN'}
              onCommit={(v) => send({ t: 'setOp', loop: loop.id, value: v })}
            />
            {loop.ratio !== null && (
              <NumberEntry
                label="R"
                value={loop.ratio}
                decimals={3}
                disabled={false}
                onCommit={(v) => send({ t: 'setRatio', loop: loop.id, value: v })}
              />
            )}
          </div>

          {loop.inGroup && (
            <div className="muted" style={{ fontSize: 10.5 }}>
              Setpoint is set by the {loop.inGroup} flow split.
            </div>
          )}
          {loop.feedforward !== null && (
            <div className="muted" style={{ fontSize: 10.5 }}>
              Feed-forward contributes {loop.feedforward.toFixed(1)} % of the output.
            </div>
          )}

          <MiniTrend
            windowS={900}
            tags={[
              { tag: `${loop.id}.PV`, colour: '#6fcf7d' },
              { tag: `${loop.id}.SP`, colour: '#7fb2d8' },
            ]}
            marks={marks.map((m) => ({ value: m.v, colour: m.colour, dashed: true }))}
          />
          <div className="muted" style={{ fontSize: 10, display: 'flex', justifyContent: 'space-between' }}>
            <span>15 min · PV/SP</span>
            <span>
              e = {loop.error.toFixed(2)} % · IAE {loop.iae.toFixed(0)}
            </span>
          </div>
        </div>
      )}

      {tab === 'tune' && <TuningTab loopId={loopId} />}

      {tab === 'info' && (
        <div style={{ padding: 11, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
          <div>
            <strong>{loop.name}</strong>
          </div>
          <div className="muted">{loop.description}</div>
          <div style={{ borderTop: '1px solid var(--line-dim)', paddingTop: 8, lineHeight: 1.5 }}>{loop.guidance}</div>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', margin: 0, fontSize: 11.5 }}>
            <dt className="muted">PV tag</dt>
            <dd className="num" style={{ margin: 0 }}>
              {loop.pvTag}
            </dd>
            <dt className="muted">Output</dt>
            <dd className="num" style={{ margin: 0 }}>
              {loop.outTag}
            </dd>
            <dt className="muted">Action</dt>
            <dd style={{ margin: 0 }}>{loop.tuning.direction}</dd>
            {loop.secondary && (
              <>
                <dt className="muted">Cascade to</dt>
                <dd style={{ margin: 0 }}>{loop.secondary}</dd>
              </>
            )}
            {loop.primary && (
              <>
                <dt className="muted">Primary</dt>
                <dd style={{ margin: 0 }}>{loop.primary}</dd>
              </>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}
